import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { MoreHorizontal, Scan, CheckCircle, Trash2, SendHorizontal, Edit3, Loader2 } from 'lucide-react';
import { prescreenAdminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'sonner';

const OUTCOME_BADGE_VARIANT = {
  DPIA_REQUIRED: 'destructive',
  DPIA_RECOMMENDED: 'warning',
  NO_REVIEW_REQUIRED: 'success',
  SPEAK_TO_DPO: 'secondary',
};

export const PreScreenTable = ({ submissions, onRefresh }) => {
  const navigate = useNavigate();
  const { isComplianceReadOnly } = useAuth();

  // Override-outcome dialog state (spec §Pre-screen table → Actions: Override outcome)
  const [overrideTarget, setOverrideTarget] = useState(null);  // submission record or null
  const [overrideOutcome, setOverrideOutcome] = useState('');
  const [overrideRationale, setOverrideRationale] = useState('');
  const [overriding, setOverriding] = useState(false);

  const openOverride = (submission) => {
    setOverrideTarget(submission);
    setOverrideOutcome(submission.dpo_override_outcome || submission.outcome || '');
    setOverrideRationale('');
  };

  const closeOverride = () => {
    setOverrideTarget(null);
    setOverrideOutcome('');
    setOverrideRationale('');
  };

  const handleOverride = async () => {
    if (!overrideOutcome || !overrideRationale.trim()) {
      toast.error('Please select an outcome and provide a rationale.');
      return;
    }
    try {
      setOverriding(true);
      await prescreenAdminAPI.overrideOutcome(overrideTarget.id, overrideOutcome, overrideRationale.trim());
      toast.success('Outcome overridden');
      closeOverride();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to override outcome');
    } finally {
      setOverriding(false);
    }
  };

  const handleTriggerIntake = async (submission) => {
    try {
      const response = await prescreenAdminAPI.createIntake(submission.id);
      toast.success('DPIA intake created');
      if (onRefresh) onRefresh();
      if (response.data?.intake_token) {
        navigate(`/risk/dpia-intake/${response.data.intake_token}`);
      }
    } catch (error) {
      toast.error('Failed to create intake');
    }
  };

  const handleDelete = async (submission) => {
    if (!window.confirm(`Delete pre-screen assessment "${submission.prescreen_reference}"?`)) {
      return;
    }
    try {
      await prescreenAdminAPI.delete(submission.id);
      toast.success('Pre-screen assessment deleted');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to delete pre-screen assessment');
    }
  };

  if (!submissions || submissions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Scan className="h-10 w-10 mx-auto mb-2 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No pre-screen submissions yet.</p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Scan className="h-4 w-4 text-primary" />
          Privacy Pre-Screen Assessments
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Submitter</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Reviewed</TableHead>
              <TableHead>DPIA</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {submissions.map((submission) => (
              <TableRow key={submission.id} className="hover:bg-muted/50">
                <TableCell className="font-mono text-sm">{submission.prescreen_reference || '-'}</TableCell>

                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{submission.submitter_name || '-'}</p>
                    <p className="text-xs text-muted-foreground">{submission.submitter_email || '-'}</p>
                  </div>
                </TableCell>

                <TableCell className="text-sm">{submission.submitter_department || '—'}</TableCell>

                <TableCell>
                  {(() => {
                    // Show overridden outcome if it exists, otherwise show original outcome
                    const displayOutcome = submission.dpo_override_outcome || submission.outcome;
                    return (
                      <Badge variant={OUTCOME_BADGE_VARIANT[displayOutcome] || 'secondary'}>
                        {displayOutcome?.replace(/_/g, ' ') || 'Unknown'}
                      </Badge>
                    );
                  })()}
                </TableCell>

                <TableCell className="text-sm">{formatDate(submission.created_at)}</TableCell>

                <TableCell className="text-center">
                  {submission.dpo_reviewed ? (
                    <CheckCircle className="h-4 w-4 text-green-600 inline" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>

                <TableCell>
                  {/* Spec Part 1 §Pre-screen table DPIA column + Part 2 note:
                      - outcome != DPIA_REQUIRED       → dash
                      - outcome == DPIA_REQUIRED,
                        linked_dpia_id empty           → 'Not initiated' warning badge
                        linked_dpia_id populated       → clickable link to the DPIA */}
                  {(() => {
                    const outcome = submission.dpo_override_outcome || submission.outcome;
                    if (outcome !== 'DPIA_REQUIRED') {
                      return <span className="text-muted-foreground text-sm">—</span>;
                    }
                    if (submission.linked_dpia_id) {
                      const label = submission.linked_dpia_reference || 'Open DPIA';
                      return (
                        <Badge
                          variant="outline"
                          className="bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/risk/dpia/${submission.linked_dpia_id}`);
                          }}
                          data-testid={`prescreen-dpia-link-${submission.id}`}
                        >
                          {label}
                        </Badge>
                      );
                    }
                    return (
                      <Badge
                        variant="outline"
                        className="bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                        data-testid={`prescreen-not-initiated-${submission.id}`}
                      >
                        Not initiated
                      </Badge>
                    );
                  })()}
                </TableCell>

                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {/* Spec §Pre-screen table — Actions: View, Override outcome, Trigger intake, Delete */}
                      <DropdownMenuItem
                        onClick={() => {
                          navigate(`/risk/prescreen/${submission.id}`);
                        }}
                      >
                        View Details
                      </DropdownMenuItem>
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem onClick={() => openOverride(submission)}>
                          <Edit3 className="h-4 w-4 mr-2" />
                          Override Outcome
                        </DropdownMenuItem>
                      )}
                      {!isComplianceReadOnly && submission.outcome === 'DPIA_REQUIRED' && !submission.intake_generated && (
                        <DropdownMenuItem onClick={() => handleTriggerIntake(submission)}>
                          <SendHorizontal className="h-4 w-4 mr-2" />
                          Trigger Intake
                        </DropdownMenuItem>
                      )}
                      {!isComplianceReadOnly && (
                        <DropdownMenuItem onClick={() => handleDelete(submission)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {/* Override-outcome dialog (spec §Pre-screen table Actions) */}
      <Dialog open={!!overrideTarget} onOpenChange={(open) => !open && closeOverride()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Override Assessment Outcome</DialogTitle>
            <DialogDescription>
              Change the automated outcome and provide a rationale for the override. The original outcome is retained in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">New Outcome</label>
              <select
                value={overrideOutcome}
                onChange={(e) => setOverrideOutcome(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                data-testid="override-outcome-select"
              >
                <option value="">Select outcome...</option>
                <option value="DPIA_REQUIRED">DPIA Required</option>
                <option value="DPIA_RECOMMENDED">DPIA Recommended</option>
                <option value="SPEAK_TO_DPO">Speak to DPO</option>
                <option value="NO_REVIEW_REQUIRED">No Review Required</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Reason for Override</label>
              <Textarea
                placeholder="Explain why you are overriding the automated outcome..."
                value={overrideRationale}
                onChange={(e) => setOverrideRationale(e.target.value)}
                className="min-h-20"
                data-testid="override-rationale-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeOverride} disabled={overriding}>Cancel</Button>
            <Button onClick={handleOverride} disabled={overriding} data-testid="override-submit-btn">
              {overriding ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Overriding...</>) : 'Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
